import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowRightLeft,
  BadgeDollarSign,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleCheck,
  CircleX,
  ClipboardCheck,
  ClipboardList,
  Eye,
  File,
  FileImage,
  FileText,
  Inbox,
  Info,
  LoaderCircle,
  Mail,
  MapPin,
  MessageSquare,
  NotebookPen,
  Paperclip,
  Pencil,
  Phone,
  RefreshCw,
  RotateCcw,
  Save,
  ShieldAlert,
  UserRound,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AttachmentPreviewModal from '../components/AttachmentPreviewModal';
import { consultationsService } from '../services/api';
import './css/AssignedRequests.css';
import {
  ORDER_STATUSES,
  REJECTION_REASONS,
  getContactedChip,
  getDealChip,
  getOrderStatusInfo,
  getRejectionLabel,
} from './utils/AssignedRequests';
import { formatDateTime } from './utils/Common';


const AssignedRequests = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [editingNotes, setEditingNotes] = useState({});
  const [editingField, setEditingField] = useState(null); // { id, field }
  const [fieldValue, setFieldValue] = useState('');
  const [rejectionModal, setRejectionModal] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'active';
  const targetId = searchParams.get('id') ? parseInt(searchParams.get('id'), 10) : null;
  const [validationError, setValidationError] = useState(null);
  const [dealAmountModal, setDealAmountModal] = useState(null);
  const [editDealAmountModal, setEditDealAmountModal] = useState(null);
  const [returnModal, setReturnModal] = useState(null);
  const [transferModal, setTransferModal] = useState(null); // { id, name, employeeId }
  const [transferSearch, setTransferSearch] = useState('');
  const [employees, setEmployees] = useState([]);
  const [reactivateModal, setReactivateModal] = useState(null);
  const [spamModal, setSpamModal] = useState(null); // { id, name, restore }
  const [previewAttachment, setPreviewAttachment] = useState(null); // { consultationId, attachmentId }
  const orderStatusIcons = {
    not_started: Circle,
    in_progress: LoaderCircle,
    completed: CircleCheck,
  };

  // Auto-dismiss validation errors after 5 seconds
  useEffect(() => {
    if (validationError) {
      const timer = setTimeout(() => setValidationError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [validationError]);

  useEffect(() => {
    fetchAssigned();
    fetchEmployees();
  }, []);

  // Deep link: expand + scroll to a specific consultation when ?id= is present
  useEffect(() => {
    if (!targetId || loading) return;
    setExpandedRows(prev => ({ ...prev, [targetId]: true }));
    setTimeout(() => {
      document.getElementById(`consultation-card-${targetId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  }, [loading, targetId]);

  const fetchAssigned = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await consultationsService.getMyAssigned();
      setConsultations(data.consultations || []);
    } catch (err) {
      console.error('Error fetching assigned consultations:', err);
      setError('Nie udało się załadować spotkań');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const data = await consultationsService.getAssignableEmployees();
      setEmployees(data.employees || []);
    } catch (err) {
      console.error('Error fetching assignable employees:', err);
    }
  };

  const handleStatusChange = async (id, field, value) => {
    try {
      setUpdating(prev => ({ ...prev, [`${id}-${field}`]: true }));
      const updated = await consultationsService.updateStatus(id, { [field]: value });
      setConsultations(prev =>
        prev.map(c => (c.id === id ? { ...c, ...updated } : c))
      );
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Nie udało się zaktualizować statusu');
    } finally {
      setUpdating(prev => ({ ...prev, [`${id}-${field}`]: false }));
    }
  };

  const handleOrderStatusChange = async (id, newStatus) => {
    if (newStatus === 'completed') {
      const consultation = consultations.find(c => c.id === id);
      if (consultation) {
        if (consultation.contacted === null || consultation.contacted === undefined) {
          setValidationError({ id, message: 'Proszę wybrać, czy skontaktowano się z klientem, przed oznaczeniem jako zakończone.' });
          return;
        }
        if (consultation.deal_signed === null || consultation.deal_signed === undefined) {
          setValidationError({ id, message: 'Proszę wybrać, czy umowa została podpisana, przed oznaczeniem jako zakończone.' });
          return;
        }
        if (consultation.deal_signed === false && !consultation.rejection_reason) {
          setValidationError({ id, message: 'Proszę podać powód odmowy dla niepodpisanej umowy, przed oznaczeniem jako zakończone.' });
          return;
        }
        if (consultation.deal_signed === true && !consultation.deal_amount) {
          setDealAmountModal({ id, amount: '' });
          return;
        }
      }
    }
    setValidationError(null);
    await handleStatusChange(id, 'order_status', newStatus);
  };

  const handleDealAmountSubmit = async () => {
    if (!dealAmountModal || !dealAmountModal.amount) return;
    const { id, amount } = dealAmountModal;
    try {
      setUpdating(prev => ({ ...prev, [`${id}-deal_amount`]: true }));
      await consultationsService.updateStatus(id, { deal_amount: parseFloat(amount), order_status: 'completed' });
      setConsultations(prev =>
        prev.map(c => (c.id === id ? { ...c, deal_amount: parseFloat(amount), order_status: 'completed' } : c))
      );
      setDealAmountModal(null);
    } catch (err) {
      console.error('Error saving deal amount:', err);
      setError('Nie udało się zapisać kwoty transakcji');
    } finally {
      setUpdating(prev => ({ ...prev, [`${id}-deal_amount`]: false }));
    }
  };

  const handleEditDealAmountSubmit = async () => {
    if (!editDealAmountModal) return;
    const { id, amount } = editDealAmountModal;
    try {
      setUpdating(prev => ({ ...prev, [`${id}-edit_deal_amount`]: true }));
      const parsedAmount = amount ? parseFloat(amount) : null;
      await consultationsService.updateStatus(id, { deal_amount: parsedAmount });
      setConsultations(prev =>
        prev.map(c => (c.id === id ? { ...c, deal_amount: parsedAmount } : c))
      );
      setEditDealAmountModal(null);
    } catch (err) {
      console.error('Error updating deal amount:', err);
      setError('Nie udało się zaktualizować kwoty transakcji');
    } finally {
      setUpdating(prev => ({ ...prev, [`${id}-edit_deal_amount`]: false }));
    }
  };

  const handleDealSignedChange = async (id, value) => {
    if (value === false) {
      setRejectionModal({ id, reason: '', comment: '' });
    } else {
      await handleStatusChange(id, 'deal_signed', true);
      await consultationsService.updateStatus(id, { rejection_reason: null, rejection_comment: null });
      setConsultations(prev =>
        prev.map(c => (c.id === id ? { ...c, deal_signed: true, rejection_reason: null, rejection_comment: null } : c))
      );
    }
  };

  const handleRejectionSubmit = async () => {
    if (!rejectionModal || !rejectionModal.reason) return;
    const { id, reason, comment } = rejectionModal;
    try {
      setUpdating(prev => ({ ...prev, [`${id}-rejection`]: true }));
      await consultationsService.updateStatus(id, {
        deal_signed: false,
        rejection_reason: reason,
        rejection_comment: reason === 'other' ? comment : null,
      });
      setConsultations(prev =>
        prev.map(c => (c.id === id ? {
          ...c,
          deal_signed: false,
          rejection_reason: reason,
          rejection_comment: reason === 'other' ? comment : null,
        } : c))
      );
      setRejectionModal(null);
    } catch (err) {
      console.error('Error saving rejection reason:', err);
      setError('Nie udało się zapisać powodu odmowy');
    } finally {
      setUpdating(prev => ({ ...prev, [`${id}-rejection`]: false }));
    }
  };

  const handleReturnConsultation = async () => {
    if (!returnModal) return;
    const { id } = returnModal;
    try {
      setUpdating(prev => ({ ...prev, [`${id}-return`]: true }));
      await consultationsService.updateStatus(id, { assigned_to_id: null });
      setConsultations(prev => prev.filter(c => c.id !== id));
      setReturnModal(null);
    } catch (err) {
      console.error('Error returning consultation:', err);
      setError('Nie udało się zwrócić konsultacji');
    } finally {
      setUpdating(prev => ({ ...prev, [`${id}-return`]: false }));
    }
  };

  const handleTransferConsultation = async () => {
    if (!transferModal?.employeeId) return;
    const { id, employeeId } = transferModal;
    try {
      setUpdating(prev => ({ ...prev, [`${id}-transfer`]: true }));
      await consultationsService.assign(id, Number(employeeId));
      setConsultations(prev => prev.filter(c => c.id !== id));
      setTransferModal(null);
      setTransferSearch('');
    } catch (err) {
      console.error('Error transferring consultation:', err);
      setError(err.response?.data?.detail || 'Nie udało się przepisać leada');
    } finally {
      setUpdating(prev => ({ ...prev, [`${id}-transfer`]: false }));
    }
  };

  const handleReactivateConsultation = async () => {
    if (!reactivateModal) return;
    const { id } = reactivateModal;
    try {
      setUpdating(prev => ({ ...prev, [`${id}-reactivate`]: true }));
      await consultationsService.updateStatus(id, { order_status: 'in_progress' });
      setConsultations(prev =>
        prev.map(c => (c.id === id ? { ...c, order_status: 'in_progress' } : c))
      );
      setReactivateModal(null);
    } catch (err) {
      console.error('Error reactivating consultation:', err);
      setError('Nie udało się przywrocić konsultacji do aktywnych');
    } finally {
      setUpdating(prev => ({ ...prev, [`${id}-reactivate`]: false }));
    }
  };

  const handleSpamChange = async () => {
    if (!spamModal) return;
    const { id, restore } = spamModal;
    try {
      setUpdating(prev => ({ ...prev, [`${id}-spam`]: true }));
      const update = restore
        ? { is_spam: false, order_status: 'in_progress' }
        : { is_spam: true };
      await consultationsService.updateStatus(id, update);
      setConsultations(prev =>
        prev.map(c => (c.id === id ? { ...c, ...update } : c))
      );
      setSpamModal(null);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (restore) {
          next.set('tab', 'active');
        }
        next.delete('id');
        return next;
      });
    } catch (err) {
      console.error('Error updating spam status:', err);
      setError(restore
        ? 'Nie udało się przywrócić leada do aktywnych'
        : 'Nie udało się oznaczyć leada jako spam');
    } finally {
      setUpdating(prev => ({ ...prev, [`${id}-spam`]: false }));
    }
  };

  const openRejectionReasonEditor = (consultation) => {
    setRejectionModal({
      id: consultation.id,
      reason: consultation.rejection_reason || '',
      comment: consultation.rejection_comment || '',
    });
  };

  const handleNotesChange = (id, value) => {
    setEditingNotes(prev => ({ ...prev, [id]: value }));
  };

  const handleNotesSave = async (id) => {
    const notes = editingNotes[id];
    if (notes === undefined) return;

    const originalConsultation = consultations.find(c => c.id === id);
    const originalNotes = originalConsultation?.notes ?? '';
    if (notes === originalNotes) {
      setEditingNotes(prev => { const u = { ...prev }; delete u[id]; return u; });
      return;
    }

    try {
      setUpdating(prev => ({ ...prev, [`${id}-notes`]: true }));
      await consultationsService.updateStatus(id, { notes: notes || null });
      setConsultations(prev =>
        prev.map(c => (c.id === id ? { ...c, notes } : c))
      );
      setEditingNotes(prev => { const u = { ...prev }; delete u[id]; return u; });
    } catch (err) {
      console.error('Error saving notes:', err);
      setError('Nie udało się zapisać notatek');
    } finally {
      setUpdating(prev => ({ ...prev, [`${id}-notes`]: false }));
    }
  };

  const toggleRow = (id) => {
    const willExpand = !expandedRows[id];
    setExpandedRows(prev => ({ ...prev, [id]: willExpand }));
    setSearchParams(sp => {
      const next = new URLSearchParams(sp);
      if (willExpand) {
        next.set('id', id);
      } else {
        next.delete('id');
      }
      return next;
    });
  };

  const startEditingField = (id, field, currentValue) => {
    setEditingField({ id, field });
    setFieldValue(currentValue || '');
  };

  const cancelEditingField = () => {
    setEditingField(null);
    setFieldValue('');
  };

  const saveField = async (id, field) => {
    try {
      setUpdating(prev => ({ ...prev, [`${id}-${field}`]: true }));
      await consultationsService.updateStatus(id, { [field]: fieldValue });
      setConsultations(prev =>
        prev.map(c => (c.id === id ? { ...c, [field]: fieldValue } : c))
      );
      setEditingField(null);
      setFieldValue('');
    } catch (err) {
      console.error(`Error updating ${field}:`, err);
      setError(`Nie udało się zaktualizować ${field}`);
    } finally {
      setUpdating(prev => ({ ...prev, [`${id}-${field}`]: false }));
    }
  };

  /* -------- Sub-components -------- */

  // Editable field component for inline editing
  const EditableField = ({ consultation, field, displayValue, className = '' }) => {
    const isEditing = editingField?.id === consultation.id && editingField?.field === field;
    const isUpdating = updating[`${consultation.id}-${field}`];

    if (consultation.source !== 'email') {
      return <span className={className}>{displayValue || '—'}</span>;
    }

    if (isEditing) {
      return (
        <div className="inline-edit-field" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            value={fieldValue}
            onChange={(e) => setFieldValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                saveField(consultation.id, field);
              } else if (e.key === 'Escape') {
                cancelEditingField();
              }
            }}
            autoFocus
            className="inline-input-field"
          />
          <div className="inline-field-actions">
            <button
              className="inline-save-field"
              onClick={() => saveField(consultation.id, field)}
              disabled={isUpdating}
              title="Zapisz (Enter)"
            >
              <Check aria-hidden="true" />
            </button>
            <button
              className="inline-cancel-field"
              onClick={cancelEditingField}
              disabled={isUpdating}
              title="Anuluj (Esc)"
            >
              <X aria-hidden="true" />
            </button>
          </div>
        </div>
      );
    }

    return (
      <span
        className={`editable-field-value ${className}`}
        onClick={(e) => {
          e.stopPropagation();
          startEditingField(consultation.id, field, displayValue);
        }}
        title="Kliknij, aby edytować"
      >
        {displayValue || <em className="empty-field-value">Kliknij</em>}
        <Pencil className="edit-field-icon" aria-hidden="true" />
      </span>
    );
  };

  const StatusToggle = ({ value, field, id }) => (
    <div className="toggle-group">
      <button
        className={`toggle-btn toggle-yes ${value === true ? 'active' : ''}`}
        onClick={() => handleStatusChange(id, field, true)}
        disabled={updating[`${id}-${field}`] || value === true}
      >
        Tak
      </button>
      <button
        className={`toggle-btn toggle-no ${value === false ? 'active' : ''}`}
        onClick={() => handleStatusChange(id, field, false)}
        disabled={updating[`${id}-${field}`] || value === false}
      >
        Nie
      </button>
    </div>
  );

  const DealSignedToggle = ({ consultation }) => (
    <div className="toggle-group">
      <button
        className={`toggle-btn toggle-yes ${consultation.deal_signed === true ? 'active' : ''}`}
        onClick={() => handleDealSignedChange(consultation.id, true)}
        disabled={updating[`${consultation.id}-deal_signed`] || consultation.deal_signed === true}
      >
        Tak
      </button>
      <button
        className={`toggle-btn toggle-no ${consultation.deal_signed === false ? 'active' : ''}`}
        onClick={() => handleDealSignedChange(consultation.id, false)}
        disabled={updating[`${consultation.id}-deal_signed`] || consultation.deal_signed === false}
      >
        Nie
      </button>
    </div>
  );

  const OrderStatusPicker = ({ consultation }) => (
    <div className="order-status-picker">
      <div className="order-pills">
        {ORDER_STATUSES.map(os => {
          const StatusIcon = orderStatusIcons[os.value];
          return (
            <button
              key={os.value}
              className={`order-pill ${consultation.order_status === os.value ? 'active' : ''} pill-${os.value}`}
              onClick={() => handleOrderStatusChange(consultation.id, os.value)}
              disabled={updating[`${consultation.id}-order_status`]}
            >
              <StatusIcon className="pill-icon" aria-hidden="true" /> {os.label}
            </button>
          );
        })}
      </div>
      {validationError && validationError.id === consultation.id && (
        <div className="validation-toast">{validationError.message}</div>
      )}
    </div>
  );

  /* -------- Filtering -------- */

  const activeMeetings = consultations.filter(c => !c.is_spam && c.order_status !== 'completed');
  const successfulMeetings = consultations.filter(c => !c.is_spam && c.order_status === 'completed' && c.deal_signed === true);
  const unsuccessfulMeetings = consultations.filter(c => !c.is_spam && c.order_status === 'completed' && c.deal_signed !== true);
  const spamMeetings = consultations.filter(c => c.is_spam);
  const normalizedTransferSearch = transferSearch.trim().toLocaleLowerCase('pl-PL');
  const transferEmployees = employees
    .filter(employee =>
      employee.id !== transferModal?.ownerId
      && (employee.full_name || '').toLocaleLowerCase('pl-PL').includes(normalizedTransferSearch)
    )
    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'pl-PL', { sensitivity: 'base' }));

  /* -------- Card-based list -------- */

  const renderCards = (data) => (
    <div className="cards-list">
      {data.map((c) => {
        const isExpanded = expandedRows[c.id];
        const statusInfo = getOrderStatusInfo(c.order_status);
        const StatusIcon = orderStatusIcons[statusInfo.value];
        const contactedChip = getContactedChip(c.contacted);
        const dealChip = getDealChip(c.deal_signed);
        return (
          <div key={c.id} id={`consultation-card-${c.id}`} className={`meeting-card ${c.order_status === 'completed' ? 'card-completed' : ''} ${c.is_spam ? 'card-spam' : ''} ${isExpanded ? 'card-open' : ''} ${transferModal?.id === c.id ? 'transfer-open' : ''}`}>
            {/* Clickable compact header — click to expand/collapse */}
            <div className="card-clickable" onClick={() => toggleRow(c.id)}>
              <div className="card-header">
                <div className="card-header-left">
                  <span className="expand-arrow">
                    {isExpanded
                      ? <ChevronDown aria-hidden="true" />
                      : <ChevronRight aria-hidden="true" />}
                  </span>
                  <span className="card-id">#{c.id}</span>
                  {c.source === 'email' ? (
                    <>
                      <EditableField 
                        consultation={c}
                        field="first_name"
                        displayValue={c.first_name}
                        className="card-name-part"
                      />
                      {' '}
                      <EditableField 
                        consultation={c}
                        field="last_name"
                        displayValue={c.last_name}
                        className="card-name-part"
                      />
                    </>
                  ) : (
                    <span className="card-name">{c.first_name} {c.last_name}</span>
                  )}
                  <span className="card-subject-inline">— {c.subject}</span>
                </div>
                <div className="card-header-right">
                  {c.assigned_at && (
                    <span className="card-assigned-date" title="Data przypisania">
                      <MapPin aria-hidden="true" /> {formatDateTime(c.assigned_at)}
                    </span>
                  )}
                  <span className="card-date" title="Data utworzenia">{formatDateTime(c.created_at)}</span>
                  <span className={`status-badge ${c.is_spam ? 'badge-spam' : `badge-${statusInfo.value}`}`}>
                    {c.is_spam
                      ? <><ShieldAlert aria-hidden="true" /> Spam</>
                      : <><StatusIcon aria-hidden="true" /> {statusInfo.label}</>}
                  </span>
                </div>
              </div>

              {/* Compact chips row — always visible */}
              <div className="card-chips">
                <span className={`chip ${contactedChip.cls}`}>{contactedChip.label}</span>
                <span className={`chip ${dealChip.cls}`}>{dealChip.label}</span>
                {c.deal_signed && c.deal_amount && (
                  <span className="chip chip-amount">
                    <BadgeDollarSign aria-hidden="true" /> {parseFloat(c.deal_amount).toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}
                  </span>
                )}
                {c.rejection_reason && !c.deal_signed && (
                  <span
                    className="chip chip-rejection chip-clickable"
                    onClick={(e) => { e.stopPropagation(); openRejectionReasonEditor(c); }}
                    title="Kliknij, aby zmienić powód odmowy"
                  >
                    <AlertTriangle aria-hidden="true" /> {getRejectionLabel(c.rejection_reason)} <Pencil aria-hidden="true" />
                  </span>
                )}
                {c.notes && (
                  <span className="chip chip-notes" title={c.notes}><NotebookPen aria-hidden="true" /> Zawiera notatki</span>
                )}
              </div>
            </div>

            {/* Expanded panel */}
            {isExpanded && (
              <div className="card-expanded">
                {/* Row 1: Actions — Contacted / Deal Signed / Order Status + Return */}
                <div className="expanded-actions-row">
                  {c.is_spam ? (
                    <div className="action-group action-group-return">
                      <div className="action-label">Spam</div>
                      <button
                        className="restore-spam-btn"
                        onClick={() => setSpamModal({ id: c.id, name: `${c.first_name} ${c.last_name}`, restore: true })}
                        disabled={updating[`${c.id}-spam`]}
                        title="Przywróć tego leada do aktywnych"
                      >
                        <RefreshCw aria-hidden="true" /> Przywróć do aktywnych
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="action-group action-group-wide">
                        <div className="action-label">Status</div>
                        <OrderStatusPicker consultation={c} />
                      </div>
                      <div className="action-group">
                        <div className="action-label">Kontakt?</div>
                        <StatusToggle value={c.contacted} field="contacted" id={c.id} />
                      </div>
                      <div className="action-group">
                        <div className="action-label">Umowa podpisana?</div>
                        <DealSignedToggle consultation={c} />
                      </div>
                      {c.order_status !== 'completed' && (
                        <div className="action-group lead-actions-group">
                          <div className="action-label">Akcje</div>
                          <div className="lead-actions-row">
                            <button
                              className="spam-btn"
                              onClick={() => setSpamModal({ id: c.id, name: `${c.first_name} ${c.last_name}`, restore: false })}
                              disabled={updating[`${c.id}-spam`]}
                              title="Oznacz tego leada jako spam"
                            >
                              <ShieldAlert aria-hidden="true" /> Spam
                            </button>
                            <div className="transfer-control">
                              <button
                                className="transfer-btn"
                                onClick={() => {
                                  if (transferModal?.id === c.id) {
                                    setTransferModal(null);
                                    setTransferSearch('');
                                  } else {
                                    setTransferSearch('');
                                    setTransferModal({ id: c.id, name: `${c.first_name} ${c.last_name}`, ownerId: c.assigned_to_id, employeeId: '' });
                                  }
                                }}
                                disabled={updating[`${c.id}-transfer`]}
                                title="Przepisz tego leada do innego handlowca"
                                aria-expanded={transferModal?.id === c.id}
                                aria-controls={transferModal?.id === c.id ? `transfer-panel-${c.id}` : undefined}
                              >
                                <ArrowRightLeft aria-hidden="true" /> Przepisz
                              </button>
                              {transferModal?.id === c.id && (
                                <section
                                  id={`transfer-panel-${c.id}`}
                                  className="transfer-panel"
                                  aria-labelledby={`transfer-panel-title-${c.id}`}
                                >
                                  <div className="transfer-panel-header">
                                    <ArrowRightLeft aria-hidden="true" />
                                    <div>
                                      <h3 id={`transfer-panel-title-${c.id}`}>Przepisz leada</h3>
                                      <p>Wybierz użytkownika, któremu chcesz przekazać leada <strong>{transferModal.name}</strong>.</p>
                                    </div>
                                  </div>
                                  <div className="transfer-panel-body">
                                    <label htmlFor={`transfer-employee-search-${c.id}`}>Wyszukaj użytkownika</label>
                                    <input
                                      id={`transfer-employee-search-${c.id}`}
                                      type="search"
                                      className="transfer-employee-search"
                                      placeholder="Wpisz imię lub nazwisko…"
                                      value={transferSearch}
                                      onChange={event => setTransferSearch(event.target.value)}
                                      autoFocus
                                    />
                                    <div className="transfer-employee-list" aria-label="Lista użytkowników">
                                      {transferEmployees.map(employee => (
                                        <button
                                          type="button"
                                          key={employee.id}
                                          className={transferModal.employeeId === String(employee.id) ? 'selected' : ''}
                                          onClick={() => setTransferModal(prev => ({ ...prev, employeeId: String(employee.id) }))}
                                        >
                                          <span>{employee.full_name}</span>
                                          {transferModal.employeeId === String(employee.id) && <Check aria-hidden="true" />}
                                        </button>
                                      ))}
                                    </div>
                                    {transferEmployees.length === 0 && (
                                      <p className="transfer-empty">Brak pasujących użytkowników.</p>
                                    )}
                                    <p className="transfer-panel-hint">Lead zniknie z Twojej listy, a wybrany użytkownik otrzyma e-mail i powiadomienie w portalu.</p>
                                  </div>
                                  <div className="transfer-panel-actions">
                                    <button
                                      className="modal-btn cancel"
                                      onClick={() => { setTransferModal(null); setTransferSearch(''); }}
                                    >
                                      Anuluj
                                    </button>
                                    <button
                                      className="modal-btn confirm confirm-blue"
                                      onClick={handleTransferConsultation}
                                      disabled={!transferModal.employeeId || updating[`${transferModal.id}-transfer`]}
                                    >
                                      {updating[`${transferModal.id}-transfer`] ? 'Przepisywanie…' : <><ArrowRightLeft aria-hidden="true" /> Przepisz</>}
                                    </button>
                                  </div>
                                </section>
                              )}
                            </div>
                            <button
                              className="return-btn"
                              onClick={() => setReturnModal({ id: c.id, name: `${c.first_name} ${c.last_name}` })}
                              disabled={updating[`${c.id}-return`]}
                              title="Zwróć tę konsultację do puli nieprzypisanych"
                            >
                              <RotateCcw aria-hidden="true" /> Zwróć
                            </button>
                          </div>
                        </div>
                      )}
                      {c.order_status === 'completed' && (
                        <div className="action-group lead-actions-group">
                          <div className="action-label">Akcje</div>
                          <div className="lead-actions-row">
                            <button
                              className="spam-btn"
                              onClick={() => setSpamModal({ id: c.id, name: `${c.first_name} ${c.last_name}`, restore: false })}
                              disabled={updating[`${c.id}-spam`]}
                              title="Oznacz tego leada jako spam"
                            >
                              <ShieldAlert aria-hidden="true" /> Spam
                            </button>
                            <button
                              className="reactivate-btn"
                              onClick={() => setReactivateModal({ id: c.id, name: `${c.first_name} ${c.last_name}` })}
                              disabled={updating[`${c.id}-reactivate`]}
                              title="Przywróć tę konsultację do aktywnych"
                            >
                              <RefreshCw aria-hidden="true" /> Do aktywnych
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Row 2: Contact Info Sidebar */}
                <div className="contact-sidebar-row">
                  <div className="detail-panel contact-panel">
                    <h4><UserRound aria-hidden="true" /> Dane kontaktowe</h4>
                    {c.source === 'email' && (
                      <div className="email-source-info-box">
                        <span className="info-icon"><Info aria-hidden="true" /></span>
                        <span className="info-text">
                          To zgłoszenie pochodzi z e-maila. Jeśli brakuje danych kontaktowych, prosimy je uzupełnić z wiadomości e-mail lub po kontakcie z klientem.
                        </span>
                      </div>
                    )}
                    <div className="contact-inline-grid">
                      <div className="contact-inline-row">
                        <strong><UserRound aria-hidden="true" /> Imię:</strong>
                        {c.source === 'email' ? (
                          <EditableField 
                            consultation={c}
                            field="first_name"
                            displayValue={c.first_name}
                          />
                        ) : (
                          <span>{c.first_name || '—'}</span>
                        )}
                      </div>
                      <div className="contact-inline-row">
                        <strong><UserRound aria-hidden="true" /> Nazwisko:</strong>
                        {c.source === 'email' ? (
                          <EditableField 
                            consultation={c}
                            field="last_name"
                            displayValue={c.last_name}
                          />
                        ) : (
                          <span>{c.last_name || '—'}</span>
                        )}
                      </div>
                      <div className="contact-inline-row">
                        <strong><Phone aria-hidden="true" /> Telefon:</strong>
                        <a href={`tel:${c.phone}`} className="contact-link">{c.phone}</a>
                      </div>
                      <div className="contact-inline-row">
                        <strong><Mail aria-hidden="true" /> Email:</strong>
                        <a href={`mailto:${c.email}`} className="contact-link">{c.email}</a>
                      </div>
                      <div className="contact-inline-row">
                        <strong><MapPin aria-hidden="true" /> Kod pocztowy:</strong>
                        {c.source === 'email' ? (
                          <EditableField 
                            consultation={c}
                            field="postcode"
                            displayValue={c.postcode}
                          />
                        ) : (
                          <span>{c.postcode || '—'}</span>
                        )}
                      </div>
                      <div className="contact-inline-row">
                        <strong><Building2 aria-hidden="true" /> Miasto:</strong>
                        {c.source === 'email' ? (
                          <EditableField 
                            consultation={c}
                            field="town"
                            displayValue={c.town}
                          />
                        ) : (
                          <span>{c.town || '—'}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 3: Message + Notes (2 columns) */}
                <div className="expanded-details-row">
                  <div className="detail-panel">
                    <h4><Mail aria-hidden="true" /> Pełna wiadomość</h4>
                    <div 
                      className="message-full"
                      dangerouslySetInnerHTML={{
                        __html: c.message || '<em>Brak wiadomości</em>'
                      }}
                    />
                  </div>
                  <div className="detail-panel">
                    <h4><NotebookPen aria-hidden="true" /> Notatki wewnętrzne</h4>
                    <textarea
                      className="notes-textarea"
                      placeholder="Dodaj notatki wewnętrzne dotyczące tego spotkania..."
                      value={editingNotes[c.id] ?? c.notes ?? ''}
                      onChange={(e) => handleNotesChange(c.id, e.target.value)}
                    />
                    <div className="notes-actions">
                      <button
                        className="save-notes-btn"
                        onClick={() => handleNotesSave(c.id)}
                        disabled={updating[`${c.id}-notes`] || editingNotes[c.id] === undefined}
                      >
                        {updating[`${c.id}-notes`] ? 'Zapisywanie…' : <><Save aria-hidden="true" /> Zapisz notatki</>}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Row 4: Attachments (if any) */}
                {c.attachments && c.attachments.length > 0 && (
                  <div className="expanded-details-row">
                    <div className="detail-panel attachments-panel">
                      <h4><Paperclip aria-hidden="true" /> Załączniki ({c.attachments.length})</h4>
                      <div className="attachments-compact-list">
                        {c.attachments.map((attachment) => (
                          <div key={attachment.id} className="attachment-compact-item">
                            <div className="attachment-compact-icon">
                              {attachment.content_type?.startsWith('image/') ? <FileImage aria-hidden="true" /> :
                               attachment.content_type === 'application/pdf' ? <FileText aria-hidden="true" /> : <File aria-hidden="true" />}
                            </div>
                            <div className="attachment-compact-info">
                              <span className="attachment-compact-name" title={attachment.original_filename}>
                                {attachment.original_filename}
                              </span>
                              <span className="attachment-compact-size">
                                {(attachment.file_size / 1024).toFixed(0)} KB
                              </span>
                            </div>
                            <div className="attachment-compact-actions">
                              <button
                                className="attachment-compact-btn download"
                                onClick={() => consultationsService.downloadAttachment(
                                  c.id, 
                                  attachment.id, 
                                  attachment.original_filename
                                )}
                                title="Pobierz"
                              >
                                <ArrowDownToLine aria-hidden="true" />
                              </button>
                              {(attachment.content_type?.startsWith('image/') || attachment.content_type === 'application/pdf') && (
                                <button
                                  className="attachment-compact-btn preview"
                                  onClick={() => setPreviewAttachment({ consultationId: c.id, attachmentId: attachment.id })}
                                  title="Podgląd"
                                >
                                  <Eye aria-hidden="true" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Conditional panels: Rejection / Deal Amount */}
                {(c.rejection_reason && !c.deal_signed) || (c.deal_signed === true && c.deal_amount) ? (
                  <div className="expanded-extra-row">
                    {c.rejection_reason && !c.deal_signed && (
                      <div className="extra-panel rejection-panel">
                        <h4><CircleX aria-hidden="true" /> Powód odmowy
                          {c.order_status !== 'completed' && (
                            <button
                              className="edit-rejection-btn"
                              onClick={() => openRejectionReasonEditor(c)}
                              title="Edytuj powód odmowy"
                            ><Pencil aria-hidden="true" /> Edytuj</button>
                          )}
                        </h4>
                        <div className="rejection-info-box">
                          <span className="rejection-label">{getRejectionLabel(c.rejection_reason)}</span>
                          {c.rejection_comment && (
                            <div className="rejection-comment-text"><MessageSquare aria-hidden="true" /> {c.rejection_comment}</div>
                          )}
                        </div>
                      </div>
                    )}
                    {c.deal_signed === true && c.deal_amount && (
                      <div className="extra-panel deal-panel">
                        <h4><BadgeDollarSign aria-hidden="true" /> Kwota transakcji
                          {c.order_status !== 'completed' && (
                            <button
                              className="edit-deal-amount-btn"
                              onClick={() => setEditDealAmountModal({ id: c.id, amount: c.deal_amount || '' })}
                              title="Edytuj kwotę transakcji"
                            ><Pencil aria-hidden="true" /> Edytuj</button>
                          )}
                        </h4>
                        <div className="deal-amount-display">
                          {parseFloat(c.deal_amount).toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  /* -------- Summary stats -------- */

  const tabCounts = {
    active: activeMeetings.length,
    successful: successfulMeetings.length,
    unsuccessful: unsuccessfulMeetings.length,
    spam: spamMeetings.length,
  };

  return (
    <div className="my-meetings-page">
      <div className="page-header">
        <div className="header-top">
          <h2 className="sales-module-title"><ClipboardCheck aria-hidden="true" /> Moje leady</h2>
          <span className="total-badge">{consultations.length} razem</span>
        </div>
        <p>Zarządzaj leadami przypisanymi do Ciebie</p>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)} aria-label="Zamknij komunikat"><X aria-hidden="true" /></button>
        </div>
      )}

      {/* Summary cards */}
      {!loading && (
        <div className="summary-row">
          <div className="summary-card summary-active">
            <div className="summary-num">{tabCounts.active}</div>
            <div className="summary-label">Aktywne</div>
          </div>
          <div className="summary-card summary-success">
            <div className="summary-num">{tabCounts.successful}</div>
            <div className="summary-label">Udane</div>
          </div>
          <div className="summary-card summary-fail">
            <div className="summary-num">{tabCounts.unsuccessful}</div>
            <div className="summary-label">Nieudane</div>
          </div>
          <div className="summary-card summary-spam">
            <div className="summary-num">{tabCounts.spam}</div>
            <div className="summary-label">Spam</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'active'); return next; })}
        >
          <ClipboardList aria-hidden="true" /> Aktywne <span className="tab-count">{tabCounts.active}</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'successful' ? 'active' : ''}`}
          onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'successful'); return next; })}
        >
          <CircleCheck aria-hidden="true" /> Udane <span className="tab-count">{tabCounts.successful}</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'unsuccessful' ? 'active' : ''}`}
          onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'unsuccessful'); return next; })}
        >
          <CircleX aria-hidden="true" /> Nieudane <span className="tab-count">{tabCounts.unsuccessful}</span>
        </button>
        <button
          className={`tab-btn tab-spam ${activeTab === 'spam' ? 'active' : ''}`}
          onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'spam'); return next; })}
        >
          <ShieldAlert aria-hidden="true" /> Spam <span className="tab-count">{tabCounts.spam}</span>
        </button>
      </div>

      {/* Content */}
      <div className="page-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner-ring" />
            <span>Ładowanie spotkań…</span>
          </div>
        ) : activeTab === 'active' ? (
          activeMeetings.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><Inbox aria-hidden="true" /></div>
              <h3>Brak aktywnych spotkań</h3>
              <p>Nie masz żadnych aktywnych wniosków o konsultacje przypisanych do Ciebie.</p>
            </div>
          ) : renderCards(activeMeetings)
        ) : activeTab === 'successful' ? (
          successfulMeetings.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><CircleCheck aria-hidden="true" /></div>
              <h3>Brak udanych transakcji</h3>
              <p>Nie masz jeszcze żadnych pomyślnie zakończonych transakcji.</p>
            </div>
          ) : renderCards(successfulMeetings)
        ) : activeTab === 'unsuccessful' ? (
          unsuccessfulMeetings.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><CircleX aria-hidden="true" /></div>
              <h3>Brak nieudanych transakcji</h3>
              <p>Nie masz żadnych nieudanych transakcji.</p>
            </div>
          ) : renderCards(unsuccessfulMeetings)
        ) : (
          spamMeetings.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><ShieldAlert aria-hidden="true" /></div>
              <h3>Brak spamu</h3>
              <p>Nie masz żadnych leadów oznaczonych jako spam.</p>
            </div>
          ) : renderCards(spamMeetings)
        )}
      </div>

      {/* Spam Confirmation Modal */}
      {spamModal && (
        <div className="modal-overlay" onClick={() => setSpamModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-icon"><ShieldAlert aria-hidden="true" /></div>
            <h3>{spamModal.restore ? 'Przywróć lead do aktywnych' : 'Oznacz lead jako spam'}</h3>
            <p>
              {spamModal.restore
                ? <>Czy na pewno chcesz przywrócić lead dla <strong>{spamModal.name}</strong> do aktywnych?</>
                : <>Czy na pewno chcesz oznaczyć lead dla <strong>{spamModal.name}</strong> jako spam?</>}
            </p>
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={() => setSpamModal(null)}>
                Anuluj
              </button>
              <button
                className={`modal-btn confirm ${spamModal.restore ? 'confirm-blue' : 'confirm-spam'}`}
                onClick={handleSpamChange}
                disabled={updating[`${spamModal.id}-spam`]}
              >
                {updating[`${spamModal.id}-spam`]
                  ? 'Zapisywanie…'
                  : spamModal.restore
                    ? <><RefreshCw aria-hidden="true" /> Przywróć</>
                    : <><ShieldAlert aria-hidden="true" /> Oznacz jako spam</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {rejectionModal && (
        <div className="modal-overlay" onClick={() => setRejectionModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-icon"><CircleX aria-hidden="true" /></div>
            <h3>Umowa niepodpisana — Powód</h3>
            <p>Proszę wybrać powód, dla którego transakcja nie doszła do skutku:</p>
            <div className="rejection-options">
              {REJECTION_REASONS.map(r => (
                <label key={r.value} className={`rejection-option ${rejectionModal.reason === r.value ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="rejection_reason"
                    value={r.value}
                    checked={rejectionModal.reason === r.value}
                    onChange={(e) => setRejectionModal(prev => ({ ...prev, reason: e.target.value }))}
                  />
                  {r.label}
                </label>
              ))}
            </div>
            {rejectionModal.reason === 'other' && (
              <textarea
                className="rejection-comment-input"
                placeholder="Proszę opisać powód..."
                value={rejectionModal.comment}
                onChange={(e) => setRejectionModal(prev => ({ ...prev, comment: e.target.value }))}
              />
            )}
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={() => setRejectionModal(null)}>
                Anuluj
              </button>
              <button
                className="modal-btn confirm"
                onClick={handleRejectionSubmit}
                disabled={!rejectionModal.reason || (rejectionModal.reason === 'other' && !rejectionModal.comment.trim()) || updating[`${rejectionModal.id}-rejection`]}
              >
                {updating[`${rejectionModal.id}-rejection`] ? 'Zapisywanie…' : 'Potwierdź'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return Consultation Modal */}
      {returnModal && (
        <div className="modal-overlay" onClick={() => setReturnModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-icon"><RotateCcw aria-hidden="true" /></div>
            <h3>Zwróć konsultację</h3>
            <p>Czy na pewno chcesz zwrócić konsultację dla <strong>{returnModal.name}</strong> do puli nieprzypisanych?</p>
            <p className="modal-hint">To usunie ją z Twojej listy spotkań.</p>
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={() => setReturnModal(null)}>
                Anuluj
              </button>
              <button
                className="modal-btn confirm confirm-orange"
                onClick={handleReturnConsultation}
                disabled={updating[`${returnModal.id}-return`]}
              >
                {updating[`${returnModal.id}-return`] ? 'Zwracanie…' : <><RotateCcw aria-hidden="true" /> Zwróć</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Consultation Modal */}
      {reactivateModal && (
        <div className="modal-overlay" onClick={() => setReactivateModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-icon"><RefreshCw aria-hidden="true" /></div>
            <h3>Przywróć do aktywnych</h3>
            <p>Czy na pewno chcesz przywrócić konsultację dla <strong>{reactivateModal.name}</strong> do aktywnych?</p>
            <p className="modal-hint">Status zostanie zmieniony na "W trakcie".</p>
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={() => setReactivateModal(null)}>
                Anuluj
              </button>
              <button
                className="modal-btn confirm confirm-blue"
                onClick={handleReactivateConsultation}
                disabled={updating[`${reactivateModal.id}-reactivate`]}
              >
                {updating[`${reactivateModal.id}-reactivate`] ? 'Przywracanie…' : <><RefreshCw aria-hidden="true" /> Przywróć</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deal Amount Modal */}
      {dealAmountModal && (
        <div className="modal-overlay" onClick={() => setDealAmountModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-icon"><BadgeDollarSign aria-hidden="true" /></div>
            <h3>Wprowadź kwotę transakcji</h3>
            <p>Proszę wprowadzić kwotę transakcji dla tej umowy:</p>
            <div className="deal-amount-input-wrapper">
              <input
                type="number"
                className="deal-amount-input"
                placeholder="0.00"
                min="0"
                step="0.01"
                value={dealAmountModal.amount}
                onChange={(e) => setDealAmountModal(prev => ({ ...prev, amount: e.target.value }))}
                autoFocus
              />
              <span className="deal-amount-currency">PLN</span>
            </div>
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={() => setDealAmountModal(null)}>
                Anuluj
              </button>
              <button
                className="modal-btn confirm confirm-green"
                onClick={handleDealAmountSubmit}
                disabled={!dealAmountModal.amount || parseFloat(dealAmountModal.amount) <= 0 || updating[`${dealAmountModal.id}-deal_amount`]}
              >
                {updating[`${dealAmountModal.id}-deal_amount`] ? 'Zapisywanie…' : 'Potwierdź i zakończ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Deal Amount Modal (for active consultations) */}
      {editDealAmountModal && (
        <div className="modal-overlay" onClick={() => setEditDealAmountModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-icon"><Pencil aria-hidden="true" /></div>
            <h3>Edytuj kwotę transakcji</h3>
            <p>Zmień kwotę transakcji dla tej umowy:</p>
            <div className="deal-amount-input-wrapper">
              <input
                type="number"
                className="deal-amount-input"
                placeholder="0.00"
                min="0"
                step="0.01"
                value={editDealAmountModal.amount}
                onChange={(e) => setEditDealAmountModal(prev => ({ ...prev, amount: e.target.value }))}
                autoFocus
              />
              <span className="deal-amount-currency">PLN</span>
            </div>
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={() => setEditDealAmountModal(null)}>
                Anuluj
              </button>
              <button
                className="modal-btn confirm confirm-green"
                onClick={handleEditDealAmountSubmit}
                disabled={updating[`${editDealAmountModal.id}-edit_deal_amount`]}
              >
                {updating[`${editDealAmountModal.id}-edit_deal_amount`] ? 'Zapisywanie…' : <><Save aria-hidden="true" /> Zapisz zmiany</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Preview Modal */}
      {previewAttachment && (
        <AttachmentPreviewModal
          consultationId={previewAttachment.consultationId}
          attachmentId={previewAttachment.attachmentId}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </div>
  );
};

export default AssignedRequests;
